"use client";
import { collectionNavigationLabels } from "@/components/collections/collectionLabels";
import React from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useEffect, useState, useCallback, useRef } from "react";
import { GetDefaultImagePath } from "../defaultImage/DefaultImage";
import { LogoPath } from "../defaultImage/DefaultPaths";
import { SanityImage } from "@/sanity/SanityImage";
import { usePathname } from "next/navigation";
import CheckLink from "../../../utils/constants/LinkCheck";
import { useSavedVehiclesCount } from "@/hooks/useSavedVehicles";
import { Heart } from "lucide-react";
import { sendGTMEvent } from "@next/third-parties/google";
import {
    FAQ_HEADER_LINK,
    SanityHeaderLink,
    type HamburgerCopyRight,
    type HeaderContent,
    type HeaderLink
} from "./headerShared";
import RouteOneFinancingButton from "@/components/finance/RouteOneFinancingButton";
import type { CollectionNavItem } from "@/types/segments";

// react-aria-components lives in the mobile menu's lazy chunk so it stays off
// the critical rendering path; warmed up on first pointer/touch contact.
const HeaderMobileMenu = dynamic(() => import("./HeaderMobileMenu"), { ssr: false });
const warmUpMobileMenu = () => void import("./HeaderMobileMenu");

interface HeaderProps {
    header: HeaderContent;
    hamBurgerCopyRight?: HamburgerCopyRight;
    buyerCollectionLinksEnabled?: boolean;
    collections?: CollectionNavItem[];
}

const isInventoryLink = (link?: HeaderLink) => {
    const href = CheckLink(link?.link);
    if (!href) return false;

    try {
        return new URL(href, "https://thiscar.com").pathname.replace(/\/$/, "") === "/cars";
    } catch {
        return href.replace(/\/$/, "") === "/cars";
    }
};

export const Header = (props: HeaderProps) => {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [menuRequested, setMenuRequested] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
    const pathname = usePathname();
    const previousPathnameRef = React.useRef(pathname);
    const isReferralPath = pathname.includes("/referral");
    const savedCount = useSavedVehiclesCount();

    const phoneNumber = "800-844-7227";
    const phoneDisplay = "1-800-THIScar";

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
        setIsShopDropdownOpen(false);
    }, []);

    const toggleMenu = useCallback(() => {
        if (isMenuOpen) {
            closeMenu();
            return;
        }

        setMenuRequested(true);
        setMenuOpen(true);
    }, [isMenuOpen, closeMenu]);

    const handleMenuOpenChange = useCallback((isOpen: boolean) => {
        if (isOpen) {
            setMenuOpen(true);
            return;
        }

        closeMenu();
    }, [closeMenu]);

    const handlePhoneClick = useCallback(() => {
        sendGTMEvent({
            event: "click_to_call",
            page_section: "header",
            device_type: typeof window !== 'undefined' && window.innerWidth < 768 ? "mobile" : "desktop"
        });
    }, []);

    const handleLinkClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            closeMenu();

            if (e?.currentTarget?.href?.includes("#")) {
                e.preventDefault();
                return;
            }

            const destinationUrl = e.currentTarget.getAttribute("href");
            const currentUrl = window.location.pathname;

            if (destinationUrl === currentUrl) {
                e.preventDefault();
            }
        },
        [closeMenu]
    );

    const toggleShopDropdown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsShopDropdownOpen((prev) => !prev);
    }, []);
    const financeLink = props.header.linkFinance?.financeLink;
    const financeTriggerRef = useRef<HTMLButtonElement>(null);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset;
            setIsScrolled(scrollTop > 0);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Handle body scroll lock for mobile menu
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [isMenuOpen]);

    // Keep menu state consistent on viewport changes (mobile -> desktop).
    useEffect(() => {
        if (!isMenuOpen) {
            return;
        }

        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                closeMenu();
            }
        };

        window.addEventListener("resize", handleResize, { passive: true });
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, [isMenuOpen, closeMenu]);

    // Close mobile menu on route changes to avoid stale open state.
    useEffect(() => {
        if (previousPathnameRef.current === pathname) {
            return;
        }

        previousPathnameRef.current = pathname;
        closeMenu();
    }, [pathname, closeMenu]);

    // Close the inventory dropdown when clicking outside.
    useEffect(() => {
        const handleClickOutside = () => {
            if (isShopDropdownOpen) {
                setIsShopDropdownOpen(false);
            }
        };

        if (isShopDropdownOpen) {
            document.addEventListener("click", handleClickOutside);
        }

        return () => document.removeEventListener("click", handleClickOutside);
    }, [isShopDropdownOpen]);

    const renderPrimaryLink = (link: HeaderLink | undefined, key: string) => {
        if (!link?.linkText?.trim()) return null;

        const collections = props.collections || [];
        const isShopLink = isInventoryLink(link);
        const linkClassName =
            "t-text-automotive-dark hover:t-text-automotive-primary t-font-semibold t-transition-colors t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 focus:t-ring-offset-white t-rounded t-px-2 t-py-1";

        if (!isShopLink) {
            return (
                <SanityHeaderLink key={key} link={link} className={linkClassName}>
                    {link.linkText}
                </SanityHeaderLink>
            );
        }

        return (
            <div key={key} className="t-relative" onClick={(event) => event.stopPropagation()}>
                <div className="t-flex t-items-center">
                    <SanityHeaderLink link={link} className={`${linkClassName} t-pr-1`}>
                        {link.linkText}
                    </SanityHeaderLink>
                    <button
                        type="button"
                        onClick={toggleShopDropdown}
                        className="t-rounded t-p-1 t-text-automotive-dark hover:t-text-automotive-primary focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2"
                        aria-expanded={isShopDropdownOpen}
                        aria-haspopup="true"
                        aria-label={`Browse ${link.linkText} collections`}>
                        <svg
                            className={`t-h-4 t-w-4 t-transition-transform ${isShopDropdownOpen ? "t-rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>

                <div hidden={!isShopDropdownOpen} className="t-absolute t-left-0 t-top-full t-z-50 t-mt-2 t-w-72 t-overflow-hidden t-rounded-xl t-border t-border-gray-200 t-bg-white t-py-2 t-shadow-elegant">
                        <Link
                            href="/cars"
                            onClick={closeMenu}
                            className="t-block t-px-4 t-py-2.5 t-text-sm t-font-semibold t-text-automotive-dark hover:t-bg-gray-50 hover:t-text-automotive-primary">
                            Shop All Vehicles
                        </Link>
                        {collections.map((segment) => (
                            <Link
                                key={segment.id}
                                href={`/cars/collections/${segment.slug}`}
                                onClick={closeMenu}
                                className="t-block t-px-4 t-py-2.5 t-text-sm t-font-semibold t-text-automotive-dark hover:t-bg-gray-50 hover:t-text-automotive-primary">
                                {collectionNavigationLabels(segment).full}
                            </Link>
                        ))}
                    </div>
            </div>
        );
    };

    return (
        <>
            <header
                className={`t-w-full t-transition-all t-duration-300 print:t-hidden t-z-50 ${isScrolled
                    ? "t-fixed t-top-0 t-bg-white/95 t-backdrop-blur-md t-shadow-elegant t-border-b t-border-gray-100"
                    : "t-relative t-bg-white"
                    }`}
                role="banner">
                <nav
                    className="t-max-w-screen-2xl t-mx-auto t-px-4"
                    role="navigation"
                    aria-label="Main navigation">
                    <div className="t-flex t-items-center t-justify-between t-h-16 lg:t-h-20">
                        {/* Mobile Menu Button - Left Side */}
                        {!isReferralPath && (
                            <button
                                type="button"
                                onClick={toggleMenu}
                                onPointerEnter={warmUpMobileMenu}
                                onTouchStart={warmUpMobileMenu}
                                onFocus={warmUpMobileMenu}
                                className="lg:t-hidden t-p-2 t-rounded-xl t-text-automotive-dark hover:t-bg-gray-100 t-transition-colors t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2"
                                aria-expanded={isMenuOpen}
                                aria-controls="mobile-menu"
                                aria-label="Toggle navigation menu">
                                <span className="t-sr-only">Open main menu</span>
                                {/* Hamburger Icon */}
                                <svg
                                    className={`t-w-6 t-h-6 t-transform t-transition-transform t-duration-200 ${isMenuOpen ? "t-rotate-90" : ""
                                        }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                                    />
                                </svg>
                            </button>
                        )}

                        {/* Logo - Center on Mobile, Left on Desktop */}
                        <div className="t-flex-1 t-flex t-justify-center lg:t-justify-start lg:t-flex-none">
                            <Link
                                href={CheckLink(props.header?.link5?.link) || "/"}
                                className="t-flex t-items-center t-transition-opacity t-duration-200 hover:t-opacity-80 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 focus:t-ring-offset-white t-rounded-lg"
                                aria-label="THIScar - Return to homepage">
                                <SanityImage
                                    src={props.header?.link5?.image}
                                    defaultImage={GetDefaultImagePath(LogoPath)}
                                    alt="THIScar - Buy and Sell Quality Used Cars Online"
                                    width={137}
                                    height={51}
                                    className="t-h-10 lg:t-h-12 t-w-auto"
                                    priority
                                    sizes="137px"
                                />
                            </Link>
                        </div>

                        {/* Desktop Navigation */}
                        {!isReferralPath && (
                            <nav
                                className="t-hidden lg:t-flex t-items-center t-space-x-8"
                                aria-label="Primary navigation">
                                {/* Dynamic Navigation Links */}
                                {renderPrimaryLink(props.header?.link1, "link1")}

                                {renderPrimaryLink(props.header?.link2, "link2")}

                                {financeLink?.linkText?.trim() && (
                                    <RouteOneFinancingButton
                                        triggerRef={financeTriggerRef}
                                        configuredUrl={financeLink.link}
                                        pageSection="header_navigation"
                                        ariaLabel={financeLink.linkText}
                                        className="t-text-automotive-dark hover:t-text-automotive-primary t-font-semibold t-transition-colors t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 focus:t-ring-offset-white t-rounded t-px-2 t-py-1">
                                        {financeLink.linkText}
                                    </RouteOneFinancingButton>
                                )}

                                {renderPrimaryLink(props.header?.link3, "link3")}

                                {renderPrimaryLink(props.header?.link4?.link4, "link4")}

                                {renderPrimaryLink(FAQ_HEADER_LINK, "faqs")}
                            </nav>
                        )}

                        {/* Phone Number and Saved Vehicles - Right Side */}
                        <div className="t-hidden lg:t-flex t-items-center t-space-x-4">
                            {/* Saved Vehicles Badge */}
                            <Link
                                href="/favorites"
                                prefetch={false}
                                className="t-relative t-flex t-items-center t-space-x-2 t-text-automotive-dark hover:t-text-automotive-primary t-transition-colors t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 focus:t-ring-offset-white t-rounded t-px-3 t-py-2"
                                aria-label={`View saved vehicles (${savedCount})`}>
                                <Heart className="t-w-5 t-h-5" fill="currentColor" strokeWidth={0} aria-hidden="true" />
                                {savedCount > 0 && (
                                    <span className="t-absolute -t-top-1 -t-right-1 t-bg-automotive-error t-text-white t-text-xs t-font-bold t-rounded-full t-w-5 t-h-5 t-flex t-items-center t-justify-center">
                                        {savedCount > 9 ? '9+' : savedCount}
                                    </span>
                                )}
                            </Link>

                            <a
                                href={`tel:${phoneNumber}`}
                                onClick={handlePhoneClick}
                                className="t-flex t-items-center t-space-x-2 t-text-automotive-primary hover:t-text-automotive-secondary t-font-semibold t-transition-colors t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 focus:t-ring-offset-white t-rounded t-px-3 t-py-2"
                                aria-label={`Call THIScar at ${phoneDisplay}`}>
                                <svg
                                    className="t-w-5 t-h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                    />
                                </svg>
                                <span>{phoneDisplay}</span>
                            </a>
                        </div>

                        {/* Mobile Phone and Saved Buttons - Right Side */}
                        <div className="lg:t-hidden t-flex t-items-center t-space-x-2">
                            {/* Mobile Saved Vehicles Button */}
                            <Link
                                href="/favorites"
                                prefetch={false}
                                className="t-relative t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-xl t-text-automotive-dark hover:t-bg-gray-100 active:t-bg-gray-200 t-transition-colors t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 focus:t-ring-offset-white t-touch-manipulation"
                                aria-label={`View saved vehicles (${savedCount})`}>
                                <Heart className="t-w-5 t-h-5" fill="currentColor" strokeWidth={0} aria-hidden="true" />
                                {savedCount > 0 && (
                                    <span className="t-absolute -t-top-1 -t-right-1 t-bg-automotive-error t-text-white t-text-xs t-font-bold t-rounded-full t-w-4 t-h-4 t-flex t-items-center t-justify-center">
                                        {savedCount > 9 ? '9+' : savedCount}
                                    </span>
                                )}
                            </Link>

                            <a
                                href={`tel:${phoneNumber}`}
                                onClick={handlePhoneClick}
                                className="t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-xl t-text-automotive-primary hover:t-bg-gray-100 active:t-bg-gray-200 t-transition-colors t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 focus:t-ring-offset-white t-touch-manipulation"
                                aria-label={`Call THIScar at ${phoneDisplay}`}>
                                <svg
                                    className="t-w-5 t-h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Mobile Menu — lazy chunk, mounted after first request */}
            {!isReferralPath && menuRequested && (
                <HeaderMobileMenu
                    isMenuOpen={isMenuOpen}
                    onOpenChange={handleMenuOpenChange}
                    closeMenu={closeMenu}
                    onLinkClick={handleLinkClick}
                    onPhoneClick={handlePhoneClick}
                    header={props.header}
                    hamBurgerCopyRight={props.hamBurgerCopyRight}
                    financeLink={financeLink}
                    onFinanceClick={() => {
                        closeMenu();
                        requestAnimationFrame(() => financeTriggerRef.current?.click());
                    }}
                    phoneNumber={phoneNumber}
                    phoneDisplay={phoneDisplay}
                />
            )}
        </>
    );
};
